// SignupPage.tsx — Self-service carrier signup
// Route: /signup
// Carriers choose a plan, enter details, pay via Stripe, and get instant access.

import React, { useState } from 'react';

const SUPABASE_URL  = process.env.REACT_APP_SUPABASE_URL || '';
const SUPABASE_ANON = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const STRIPE_PUB    = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';

const PLANS = [
  {
    id:          'solo',
    name:        'Solo',
    price:       199,
    startup:     499,
    trucks:      '1–3 trucks',
    drivers:     3,
    highlight:   false,
    features:    ['Full TMS platform','Jake voice AI','3 driver app accounts','DAT + Truckstop load boards','Email-to-load automation','Factoring integration','IFTA + QuickBooks export','Customer tracking portal'],
  },
  {
    id:          'small_fleet',
    name:        'Small Fleet',
    price:       349,
    startup:     799,
    trucks:      '4–10 trucks',
    drivers:     10,
    highlight:   true,
    features:    ['Everything in Solo','10 driver app accounts','EDI 204/214/990','Driver retention scoring','Weekly financial brief','Priority support (4hr response)'],
  },
  {
    id:          'growth',
    name:        'Growth',
    price:       599,
    startup:     1499,
    trucks:      '11–25 trucks',
    drivers:     25,
    highlight:   false,
    features:    ['Everything in Small Fleet','25 driver app accounts','BOL/POD auto-OCR','3 dispatcher accounts','Custom inbound email address','Onboarding call included'],
  },
  {
    id:          'fleet',
    name:        'Fleet',
    price:       999,
    startup:     2499,
    trucks:      '26–50 trucks',
    drivers:     50,
    highlight:   false,
    features:    ['Everything in Growth','Unlimited driver accounts','5 dispatcher accounts','Phone support','Dedicated onboarding + 30-day check-in'],
  },
];

const S = {
  root:      { minHeight:'100vh', background:'#000', color:'#fff', fontFamily:'monospace' } as React.CSSProperties,
  header:    { borderBottom:'1px solid #1a1a1a', padding:'20px 32px', display:'flex', alignItems:'center', justifyContent:'space-between' } as React.CSSProperties,
  logo:      { color:'#32CD32', fontSize:'16px', fontWeight:700, letterSpacing:'4px' } as React.CSSProperties,
  body:      { maxWidth:'1100px', margin:'0 auto', padding:'48px 24px' } as React.CSSProperties,
  h1:        { fontSize:'28px', fontWeight:700, marginBottom:'8px', letterSpacing:'1px' } as React.CSSProperties,
  sub:       { color:'#555', fontSize:'14px', marginBottom:'48px' } as React.CSSProperties,
  grid:      { display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(240px, 1fr))', gap:'16px', marginBottom:'48px' } as React.CSSProperties,
  card:      (h: boolean) => ({ background:'#050505', border:`1px solid ${h ? '#32CD32' : '#1a1a1a'}`, padding:'24px', cursor:'pointer', position:'relative' as const }),
  selected:  { border:'2px solid #32CD32', background:'#050505' } as React.CSSProperties,
  badge:     { position:'absolute' as const, top:'-1px', right:'16px', background:'#32CD32', color:'#000', fontSize:'9px', fontWeight:700, letterSpacing:'1px', padding:'3px 8px' },
  planName:  { fontSize:'18px', fontWeight:700, marginBottom:'4px' } as React.CSSProperties,
  trucks:    { color:'#555', fontSize:'11px', marginBottom:'16px' } as React.CSSProperties,
  price:     { color:'#32CD32', fontSize:'28px', fontWeight:700 } as React.CSSProperties,
  priceUnit: { color:'#555', fontSize:'12px' } as React.CSSProperties,
  startup:   { color:'#555', fontSize:'11px', marginBottom:'16px' } as React.CSSProperties,
  feat:      { color:'#aaa', fontSize:'11px', margin:'4px 0' } as React.CSSProperties,
  form:      { background:'#050505', border:'1px solid #1a1a1a', padding:'32px', maxWidth:'480px' } as React.CSSProperties,
  label:     { color:'#777', fontSize:'10px', letterSpacing:'1px', marginBottom:'6px', display:'block' } as React.CSSProperties,
  input:     { background:'#000', border:'1px solid #333', color:'#fff', padding:'12px', fontFamily:'monospace', fontSize:'14px', width:'100%', boxSizing:'border-box' as const, outline:'none', marginBottom:'12px' },
  btn:       { background:'#32CD32', color:'#000', border:'none', padding:'16px', fontWeight:700, fontFamily:'monospace', fontSize:'13px', letterSpacing:'1px', width:'100%', cursor:'pointer' } as React.CSSProperties,
  err:       { color:'#cc3333', fontSize:'12px', marginBottom:'12px' } as React.CSSProperties,
  trial:     { background:'#0a0a0a', border:'1px solid #1a1a1a', padding:'16px', marginBottom:'20px', color:'#555', fontSize:'11px', lineHeight:'1.7' } as React.CSSProperties,
};

export default function SignupPage() {
  const [step, setStep] = useState<'plan' | 'details' | 'done'>('plan');
  const [selectedPlan, setSelectedPlan] = useState('small_fleet');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [createdSlug, setCreatedSlug] = useState('');

  const plan = PLANS.find(p => p.id === selectedPlan)!;

  const handleSignup = async () => {
    setError('');
    if (!company.trim()) { setError('Company name is required'); return; }
    if (!email.trim())   { setError('Email is required'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (password !== confirm) { setError('Passwords do not match'); return; }

    setLoading(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/create-company`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'apikey': SUPABASE_ANON },
        body: JSON.stringify({ email: email.trim(), password, companyName: company.trim(), plan: selectedPlan }),
      });
      const data = await res.json();
      if (!res.ok || data.error) { setError(data.error || 'Signup failed. Please try again.'); setLoading(false); return; }

      setCreatedSlug(data.emailSlug || '');

      // If Stripe is configured, redirect to checkout for payment
      if (STRIPE_PUB && plan.price > 0) {
        const checkoutRes = await fetch(`${SUPABASE_URL}/functions/v1/create-checkout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'apikey': SUPABASE_ANON },
          body: JSON.stringify({ plan: selectedPlan, companyId: data.companyId, email: email.trim() }),
        });
        const checkout = await checkoutRes.json();
        if (checkout.url) { window.location.href = checkout.url; return; }
      }

      // No Stripe configured — trial starts immediately
      setStep('done');
    } catch (e: any) {
      setError(e.message || 'Something went wrong');
    }
    setLoading(false);
  };

  if (step === 'done') return (
    <div style={S.root}>
      <div style={S.header}><div style={S.logo}>BLACK TOP TMS</div></div>
      <div style={{ ...S.body, textAlign:'center' as const, paddingTop:'80px' }}>
        <div style={{ color:'#32CD32', fontSize:'48px', marginBottom:'16px' }}>✓</div>
        <div style={{ fontSize:'24px', fontWeight:700, marginBottom:'8px' }}>You're live.</div>
        <div style={{ color:'#555', marginBottom:'32px' }}>Your 14-day free trial has started. No card required to begin.</div>
        <div style={{ background:'#050505', border:'1px solid #1a1a1a', padding:'24px', display:'inline-block', textAlign:'left' as const, marginBottom:'32px' }}>
          <div style={{ color:'#777', fontSize:'10px', letterSpacing:'1px', marginBottom:'12px' }}>NEXT STEPS</div>
          <div style={{ color:'#aaa', fontSize:'13px', lineHeight:'2' }}>
            1. Log in at <a href="https://app.blacktopsystem.com" style={{ color:'#32CD32' }}>app.blacktopsystem.com</a><br/>
            2. Add your drivers in Driver Management<br/>
            3. Connect your ELD in Settings<br/>
            4. Give brokers your email: <span style={{ color:'#32CD32' }}>{createdSlug}@loads.blacktopsystem.com</span><br/>
            5. Click Activate Jake and say hello
          </div>
        </div>
        <br/>
        <a href="https://app.blacktopsystem.com" style={{ background:'#32CD32', color:'#000', padding:'16px 32px', fontWeight:700, fontFamily:'monospace', fontSize:'13px', letterSpacing:'1px', textDecoration:'none' }}>OPEN COMMAND CENTER →</a>
      </div>
    </div>
  );

  return (
    <div style={S.root}>
      <div style={S.header}>
        <div style={S.logo}>BLACK TOP TMS</div>
        <a href="https://app.blacktopsystem.com" style={{ color:'#555', fontSize:'11px', textDecoration:'none', fontFamily:'monospace' }}>Already have an account? Sign in →</a>
      </div>

      <div style={S.body}>
        {step === 'plan' && (
          <>
            <div style={S.h1}>Choose your plan</div>
            <div style={S.sub}>All plans include the full platform. 14-day free trial, no card required to start.</div>
            <div style={S.grid}>
              {PLANS.map(p => (
                <div key={p.id} onClick={() => setSelectedPlan(p.id)} style={{ ...S.card(p.highlight), ...(selectedPlan === p.id ? S.selected : {}) }}>
                  {p.highlight && <div style={S.badge}>MOST POPULAR</div>}
                  {selectedPlan === p.id && <div style={{ position:'absolute' as const, top:'12px', right:'12px', color:'#32CD32', fontSize:'16px' }}>✓</div>}
                  <div style={S.planName}>{p.name}</div>
                  <div style={S.trucks}>{p.trucks}</div>
                  <div style={S.price}>${p.price}<span style={S.priceUnit}>/mo</span></div>
                  <div style={S.startup}>+ ${p.startup.toLocaleString()} startup fee</div>
                  <div style={{ borderTop:'1px solid #111', paddingTop:'12px' }}>
                    {p.features.map((f, i) => <div key={i} style={S.feat}>✓ {f}</div>)}
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => setStep('details')} style={{ ...S.btn, maxWidth:'320px', fontSize:'14px', padding:'18px' }}>
              START WITH {plan.name.toUpperCase()} →
            </button>
            <div style={{ color:'#333', fontSize:'11px', marginTop:'12px' }}>14-day free trial · No credit card required to start · Cancel anytime</div>
          </>
        )}

        {step === 'details' && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'48px', alignItems:'start' }}>
            <div style={S.form}>
              <div style={{ color:'#32CD32', fontSize:'13px', fontWeight:700, letterSpacing:'2px', marginBottom:'24px' }}>CREATE YOUR ACCOUNT</div>
              <div style={S.trial}>
                <strong style={{ color:'#32CD32' }}>14-day free trial</strong> — no card required. You'll be prompted to add billing info after your trial ends to continue using the platform.
              </div>
              <label style={S.label}>COMPANY NAME</label>
              <input type="text" placeholder="DNA Global Enterprises" style={S.input} value={company} onChange={e => setCompany(e.target.value)} />
              <label style={S.label}>EMAIL ADDRESS</label>
              <input type="email" placeholder="you@yourcompany.com" style={S.input} value={email} onChange={e => setEmail(e.target.value)} />
              <label style={S.label}>PASSWORD</label>
              <input type="password" placeholder="8+ characters" style={S.input} value={password} onChange={e => setPassword(e.target.value)} />
              <label style={S.label}>CONFIRM PASSWORD</label>
              <input type="password" placeholder="Confirm password" style={{ ...S.input, marginBottom:'20px' }} value={confirm} onChange={e => setConfirm(e.target.value)} />
              {error && <div style={S.err}>{error}</div>}
              <button onClick={handleSignup} disabled={loading} style={S.btn}>{loading ? 'CREATING ACCOUNT...' : 'START FREE TRIAL →'}</button>
              <div style={{ color:'#555', fontSize:'10px', marginTop:'12px', lineHeight:'1.6' }}>
                By signing up you agree to our{' '}
                <a href="/terms" target="_blank" rel="noreferrer" style={{ color:'#32CD32' }}>Terms of Service</a>
                {' '}and{' '}
                <a href="/privacy" target="_blank" rel="noreferrer" style={{ color:'#32CD32' }}>Privacy Policy</a>.
                Your 14-day trial begins immediately. No charges until trial ends.
              </div>
            </div>
            <div>
              <div style={{ color:'#32CD32', fontSize:'11px', fontWeight:700, letterSpacing:'2px', marginBottom:'16px' }}>YOUR PLAN</div>
              <div style={{ background:'#050505', border:'1px solid #32CD32', padding:'20px', marginBottom:'16px' }}>
                <div style={{ fontSize:'18px', fontWeight:700, marginBottom:'4px' }}>{plan.name}</div>
                <div style={{ color:'#555', fontSize:'12px', marginBottom:'16px' }}>{plan.trucks}</div>
                <div style={{ color:'#32CD32', fontSize:'24px', fontWeight:700, marginBottom:'4px' }}>${plan.price}/mo</div>
                <div style={{ color:'#555', fontSize:'11px', marginBottom:'16px' }}>+ ${plan.startup.toLocaleString()} startup fee (billed after trial)</div>
                {plan.features.map((f, i) => <div key={i} style={{ color:'#aaa', fontSize:'11px', margin:'4px 0' }}>✓ {f}</div>)}
              </div>
              <button onClick={() => setStep('plan')} style={{ background:'transparent', border:'1px solid #333', color:'#555', padding:'10px 16px', fontFamily:'monospace', fontSize:'11px', cursor:'pointer', width:'100%' }}>
                ← CHANGE PLAN
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
